import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import { makeStyles } from "@material-ui/core/styles";

export type ProgressState =
  | "complete"
  | "current"
  | "upcoming"
  | "declined"
  | "failed";

export interface ProgressTimestamp {
  display: string;
  dateTime?: string;
  precision: "date" | "datetime";
}

export interface ProgressStep {
  id: string;
  number: number;
  title: string;
  description: string;
  state: ProgressState;
  chip: "Completed" | "In progress" | "Pending" | "Declined";
  timestamp?: ProgressTimestamp;
  icon?: "payment" | "processing" | "request";
}

interface Props {
  steps: ProgressStep[];
  ariaLabel: string;
  showChips?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  step: {
    position: "relative",
    display: "flex",
    minHeight: 88,
    paddingBottom: theme.spacing(2),
  },
  stepLast: {
    minHeight: 0,
    paddingBottom: 0,
  },
  marker: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flex: "0 0 32px",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: "50%",
    boxSizing: "border-box",
    fontSize: 13,
    fontWeight: 700,
  },
  markerComplete: {
    color: "#ffffff",
    background: "#256141",
  },
  markerCurrent: {
    color: "#ffffff",
    background: "#94680C",
    animation: "$pulse 2s ease-in-out infinite",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },
  markerUpcoming: {
    color: "#5F5D57",
    background: "#F7F6F2",
    border: "1px dashed #C9C6BD",
  },
  markerDeclined: {
    color: "#ffffff",
    background: "#C0392B",
  },
  content: {
    minWidth: 0,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1),
  },
  titleRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    margin: 0,
    color: "#171D25",
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.35,
  },
  titleCurrent: {
    color: "#765307",
  },
  titleUpcoming: {
    color: "#5F625F",
  },
  titleDeclined: {
    color: "#C0392B",
  },
  description: {
    maxWidth: 520,
    marginTop: 4,
    color: "#626871",
    fontSize: 13,
    lineHeight: 1.45,
  },
  descriptionUpcoming: {
    color: "#6B6B65",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 22,
    padding: theme.spacing(0, 1),
    border: "1px solid transparent",
    borderRadius: 6,
    boxSizing: "border-box",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  chipComplete: {
    color: "#176B48",
    background: "#E4F5EC",
    borderColor: "#B5E3C9",
  },
  chipCurrent: {
    color: "#694B05",
    background: "#FFF3D8",
    borderColor: "#E8C978",
  },
  chipUpcoming: {
    color: "#5F5D57",
    background: "#F0EFEC",
    borderColor: "#DFDDD7",
  },
  chipDeclined: {
    color: "#B33D32",
    background: "#FCE8E6",
    borderColor: "#F3B9B4",
  },
  timestamp: {
    display: "block",
    marginTop: 6,
    color: "#5F6468",
    fontSize: 12,
    lineHeight: 1.35,
  },
  connector: {
    position: "absolute",
    top: 32,
    bottom: 0,
    left: 15,
    width: 2,
    background: "#D9D7D0",
  },
  connectorComplete: {
    background: "#256141",
  },
  "@keyframes pulse": {
    "0%, 100%": {
      boxShadow: "0 0 0 0 rgba(200, 147, 42, 0.28)",
    },
    "50%": {
      boxShadow: "0 0 0 5px rgba(200, 147, 42, 0.12)",
    },
  },
}));

function isDeclined(state: ProgressState): boolean {
  return state === "declined" || state === "failed";
}

function getMarkerClass(
  state: ProgressState,
  classes: ReturnType<typeof useStyles>
) {
  if (state === "complete") return classes.markerComplete;
  if (state === "current") return classes.markerCurrent;
  if (isDeclined(state)) return classes.markerDeclined;
  return classes.markerUpcoming;
}

function getTitleClass(
  state: ProgressState,
  classes: ReturnType<typeof useStyles>
) {
  if (state === "current") return classes.titleCurrent;
  if (isDeclined(state)) return classes.titleDeclined;
  if (state === "upcoming") return classes.titleUpcoming;
  return "";
}

function getChipClass(
  state: ProgressState,
  classes: ReturnType<typeof useStyles>
) {
  if (state === "complete") return classes.chipComplete;
  if (state === "current") return classes.chipCurrent;
  if (isDeclined(state)) return classes.chipDeclined;
  return classes.chipUpcoming;
}

function getIcon(step: ProgressStep) {
  if (step.state === "complete") {
    return <CheckCircleOutlineIcon fontSize="small" aria-hidden="true" />;
  }

  if (isDeclined(step.state)) {
    return <CancelIcon fontSize="small" aria-hidden="true" />;
  }

  if (step.state === "current") {
    if (step.icon === "payment") {
      return <VerifiedUserOutlinedIcon fontSize="small" aria-hidden="true" />;
    }

    if (step.icon === "processing") {
      return <HourglassEmptyOutlinedIcon fontSize="small" aria-hidden="true" />;
    }

    return <ScheduleOutlinedIcon fontSize="small" aria-hidden="true" />;
  }

  return <span aria-hidden="true">{step.number}</span>;
}

export function TransactionProgressTracker({
  steps,
  ariaLabel,
  showChips = true,
}: Props): JSX.Element {
  const classes = useStyles();

  return (
    <ol className={classes.root} aria-label={ariaLabel}>
      {steps.map((step, index) => {
        const declined = isDeclined(step.state);
        const markerClass = getMarkerClass(step.state, classes);
        const titleClass = getTitleClass(step.state, classes);
        const chipClass = getChipClass(step.state, classes);
        const accessibleLabel = `${step.title}: ${step.chip}${
          step.timestamp ? `, ${step.timestamp.display}` : ""
        }`;

        return (
          <li
            key={step.id}
            className={`${classes.step} ${
              index === steps.length - 1 ? classes.stepLast : ""
            }`}
            aria-label={accessibleLabel}
            aria-current={step.state === "current" ? "step" : undefined}
          >
            <div className={`${classes.marker} ${markerClass}`}>
              {getIcon(step)}
            </div>
            <div className={classes.content}>
              <div className={classes.titleRow}>
                <h3 className={`${classes.title} ${titleClass}`}>
                  {step.title}
                </h3>
                {showChips && (
                  <span className={`${classes.chip} ${chipClass}`}>
                    {step.chip}
                  </span>
                )}
              </div>
              <div
                className={`${classes.description} ${
                  step.state === "upcoming" ? classes.descriptionUpcoming : ""
                }`}
              >
                {step.description}
              </div>
              {step.timestamp &&
                (step.timestamp.dateTime ? (
                  <time
                    className={classes.timestamp}
                    dateTime={step.timestamp.dateTime}
                  >
                    {step.timestamp.display}
                  </time>
                ) : (
                  <span className={classes.timestamp}>
                    {step.timestamp.display}
                  </span>
                ))}
            </div>
            {index < steps.length - 1 && (
              <span
                className={`${classes.connector} ${
                  step.state === "complete" && !declined
                    ? classes.connectorComplete
                    : ""
                }`}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
